// File: src/utils/landingSpeech.js

const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;

/**
 * Mobile-safe audio unlock trigger. Must be called inside a user gesture handler (e.g. onClick).
 */
export const unlockLandingAudio = () => {
    if (!synth) return;
    try {
        const dummyUtterance = new SpeechSynthesisUtterance('');
        dummyUtterance.volume = 0;
        synth.speak(dummyUtterance);
    } catch (e) {
        console.warn("Speech unlock warning:", e);
    }
};

/**
 * Speak the provided text using WebSpeech API with an Indian voice if available.
 */
export const playLandingSpeech = ({ text, onStart, onEnd, onError, onProgress }) => {
    if (!synth) {
        if (onError) onError("Speech synthesis is not supported on this browser.");
        return;
    }

    // Stop any existing speech
    stopLandingSpeech();

    const utterance = new SpeechSynthesisUtterance(text);

    const voices = synth.getVoices();

    // Priority: Female Hindi > Male Hindi > en-IN
    const getVoiceScore = (voice) => {
        let score = 0;
        const name = voice.name.toLowerCase();
        const lang = voice.lang.toLowerCase();

        if (lang.includes('hi-in')) score += 100;
        else if (lang.includes('en-in')) score += 50;
        else return -1; // Not an Indian voice

        // Attempt to prioritize female based on common TTS names/flags
        const femaleIndicators = ['female', 'lekha', 'kalpana', 'aditi', 'swara', 'veena', 'kavita'];
        const maleIndicators = ['male', 'hemant', 'ravi', 'neel', 'amit'];

        if (femaleIndicators.some(indicator => name.includes(indicator))) {
            score += 20;
        } else if (maleIndicators.some(indicator => name.includes(indicator))) {
            score -= 10;
        }

        // Prefer Google voices due to higher quality
        if (name.includes('google')) {
            score += 5;
        }

        return score;
    };

    const bestVoices = voices
        .map(v => ({ voice: v, score: getVoiceScore(v) }))
        .filter(v => v.score > -1)
        .sort((a, b) => b.score - a.score);

    if (bestVoices.length > 0) {
        utterance.voice = bestVoices[0].voice;
    }

    utterance.rate = 1.2;    // Slightly Faster (1.2x)
    utterance.pitch = 0.8;   // Slightly lower pitch
    utterance.volume = 1.0;  // Full volume

    utterance.onstart = () => {
        if (onStart) onStart();
    };

    utterance.onend = () => {
        if (onEnd) onEnd();
    };

    utterance.onboundary = (event) => {
        if (event.name === 'word' && onProgress) {
            onProgress(event.charIndex / text.length);
        }
    };

    utterance.onerror = (err) => {
        if (onError) onError(err);
    };

    synth.speak(utterance);
};

export const stopLandingSpeech = () => {
    if (!synth) return;
    try {
        synth.cancel();
    } catch (e) {
        console.warn("Speech stop warning:", e);
    }
};

export const pauseLandingSpeech = () => {
    if (!synth) return;
    try {
        synth.pause();
    } catch (e) { }
};

export const resumeLandingSpeech = () => {
    if (!synth) return;
    try {
        synth.resume();
    } catch (e) { }
};
